import { api, ProjectSummary, ProjectDetail } from '../api';
import { navigate } from '../lib/router';
import { escapeHtml, timeAgo } from '../lib/utils';
import { openModal } from '../components/modal';
import { toast } from '../components/toast';

export async function renderDashboard(app: HTMLElement): Promise<void> {
  app.innerHTML = `<div class="container"><div class="spinner">Loading your projects…</div></div>`;

  let projects: ProjectSummary[];
  try {
    const res = await api.get<{ projects: ProjectSummary[] }>('/projects');
    projects = res.projects;
  } catch (e: any) {
    app.innerHTML = `
      <div class="container">
        <div class="error-state">
          <h3>Could not load projects</h3>
          <p class="muted">${escapeHtml(e.message)}</p>
          <button class="btn btn-primary" id="retry">Try again</button>
        </div>
      </div>
    `;
    app.querySelector('#retry')!.addEventListener('click', () => renderDashboard(app));
    return;
  }

  app.innerHTML = `
    <div class="container">
      <div class="page-head">
        <div>
          <h1>My Projects</h1>
          <p class="muted" style="margin:0;">Create and manage any number of projects.</p>
        </div>
        <button class="btn btn-primary" id="new-project">+ New Project</button>
      </div>
      ${
        projects.length === 0
          ? `
        <div class="empty-state">
          <h3>No projects yet</h3>
          <p class="muted">Create your first project to start tracking stories and story points.</p>
          <button class="btn btn-primary" id="new-project-empty">+ New Project</button>
        </div>
      `
          : `
        <div class="project-grid">
          ${projects
            .map(
              (p) => `
            <a class="project-card" href="#/projects/${p.id}/overview">
              <h3>${escapeHtml(p.name)} ${
                (p.visibility === 'public'
                  ? '<span class="badge" style="color:#6d28d9;background:#f3e8ff;font-size:11px;padding:2px 8px;vertical-align:middle;">Public</span>'
                  : '') +
                (p.access === 'member'
                  ? '<span class="badge" style="color:#0e7490;background:#ecfeff;font-size:11px;padding:2px 8px;vertical-align:middle;">Member</span>'
                  : '')
              }</h3>
              <p>${escapeHtml(p.description) || '<span class="muted">No description</span>'}</p>
              <div class="project-meta">
                <span>${p.taskCount} task${p.taskCount === 1 ? '' : 's'}</span>
                <span>${p.memberCount} member${p.memberCount === 1 ? '' : 's'}</span>
                <span style="margin-left:auto;">${timeAgo(p.updatedAt)}</span>
              </div>
            </a>
          `
            )
            .join('')}
          <button class="project-card new-project-card" id="new-project-card">
            <span class="plus">+</span>
            <span>New Project</span>
          </button>
        </div>
      `
      }
    </div>
  `;

  const openCreate = () => openCreateProjectModal();
  app.querySelector('#new-project')?.addEventListener('click', openCreate);
  app.querySelector('#new-project-empty')?.addEventListener('click', openCreate);
  app.querySelector('#new-project-card')?.addEventListener('click', openCreate);
}

function openCreateProjectModal(): void {
  openModal(
    `
    <h2 style="font-size:20px;margin-bottom:16px;">New Project</h2>
    <form id="create-project-form">
      <div class="field">
        <label>Project name</label>
        <input class="input" name="name" placeholder="e.g. Website Redesign" />
      </div>
      <div class="field">
        <label>What is this project about?</label>
        <textarea class="textarea" name="description" rows="4" placeholder="A short description of the goal…"></textarea>
      </div>
      <div class="field">
        <label>Visibility</label>
        <select class="select" name="visibility">
          <option value="private">Private — only you and members you add can view it</option>
          <option value="public">Public — anyone can view it in read-only mode</option>
        </select>
      </div>
      <div class="form-error hidden" id="project-error"></div>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn-ghost" data-close>Cancel</button>
        <button type="submit" class="btn btn-primary">Create project</button>
      </div>
    </form>
  `,
    (body, close) => {
      const form = body.querySelector('#create-project-form') as HTMLFormElement;
      const errorEl = body.querySelector('#project-error') as HTMLElement;
      body.querySelector('[data-close]')!.addEventListener('click', close);
      const nameInput = body.querySelector<HTMLInputElement>('input[name="name"]');
      nameInput?.focus();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.classList.add('hidden');
        const data = new FormData(form);
        const name = String(data.get('name') || '').trim();
        if (!name) {
          errorEl.textContent = 'Project name is required';
          errorEl.classList.remove('hidden');
          return;
        }
        try {
          const res = await api.post<{ project: ProjectDetail }>('/projects', {
            name,
            description: String(data.get('description') || ''),
            visibility: String(data.get('visibility') || 'private'),
          });
          toast('Project created');
          close();
          navigate(`/projects/${res.project.id}/overview`);
        } catch (e: any) {
          errorEl.textContent = e.message || 'Could not create project';
          errorEl.classList.remove('hidden');
        }
      });
    }
  );
}
